import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Paper,
    Snackbar,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    Typography,
} from '@mui/material';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ticketService from '../services/TicketService';
import NewTicketDialog from './NewTicketDialog';
import HelpButton from './HelpButton';
import Pagination from './Pagination';

function formatEpoch(value) {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return '-';

    // Heuristic: seconds if it looks like 10 digits, ms if 13 digits.
    const ms = n < 1e12 ? n * 1000 : n;
    try {
        return new Date(ms).toLocaleString();
    } catch (e) {
        return String(value);
    }
}

const defaultLoadTickets = (params) => ticketService.getTickets(params);
const defaultCreateTicket = (payload) => ticketService.createTicket(payload);
const defaultLoadSupportTickets = (params) => ticketService.getSupportTickets(params);

function readPage(data) {
    const items = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
    const total = Number.isFinite(data?.total) ? data.total : items.length;
    return { items, total };
}

function formatEstimatedHours(value) {
    const n = Number(value);
    if (!Number.isFinite(n) || n < 0) return '-';
    return `${n.toLocaleString(undefined, { maximumFractionDigits: 2 })} h`;
}

function normalizePriority(value) {
    const raw = String(value || '').trim();
    const lowered = raw.toLowerCase();

    if (lowered === 'critical' || lowered === 'crititcal') return 'Critical';
    if (lowered === 'high') return 'High';
    if (lowered === 'medium') return 'Medium';
    if (lowered === 'low') return 'Low';

    return raw || '-';
}

function normalizeTicketType(value) {
    const raw = String(value || '').trim().toLowerCase();

    if (raw === 'feature') return 'Feature';
    if (raw === 'enhancement') return 'Enhancement';
    if (raw === 'bug') return 'Bug';

    return 'Bug';
}

function isClosedStatus(value) {
    const raw = String(value || '').trim().toLowerCase();
    return raw === 'close' || raw === 'closed';
}

function getPrioritySx(priority) {
    const normalized = normalizePriority(priority);

    switch (normalized) {
        case 'Critical':
            return {
                bgcolor: '#7f1d1d',
                color: '#fff',
            };
        case 'High':
            return {
                bgcolor: '#dc2626',
                color: '#fff',
            };
        case 'Medium':
            return {
                bgcolor: '#f59e0b',
                color: '#111827',
            };
        case 'Low':
            return {
                bgcolor: '#16a34a',
                color: '#fff',
            };
        default:
            return {
                bgcolor: 'grey.100',
                color: 'text.secondary',
            };
    }
}

function priorityLabel(t, value) {
    switch (normalizePriority(value)) {
        case 'Critical':
            return t('tickets.priorityCritical', 'Critical');
        case 'High':
            return t('tickets.priorityHigh', 'High');
        case 'Medium':
            return t('tickets.priorityMedium', 'Medium');
        case 'Low':
            return t('tickets.priorityLow', 'Low');
        default:
            return normalizePriority(value);
    }
}

function typeLabel(t, value) {
    switch (normalizeTicketType(value)) {
        case 'Bug':
            return t('tickets.typeBug', 'Bug');
        case 'Feature':
            return t('tickets.typeFeature', 'Feature');
        case 'Enhancement':
            return t('tickets.typeEnhancement', 'Enhancement');
        default:
            return normalizeTicketType(value);
    }
}

function statusLabel(t, value) {
    const raw = String(value || '').trim();
    if (!raw) return '-';
    switch (raw.toLowerCase()) {
        case 'open':
            return t('tickets.statusOpen', 'Open');
        case 'inprogress':
        case 'in_progress':
        case 'in-progress':
            return t('tickets.statusInProgress', 'In Progress');
        case 'in qa':
        case 'inqa':
        case 'in_qa':
        case 'in-qa':
            return t('tickets.statusInQa', 'In QA');
        case 'close':
        case 'closed':
            return t('tickets.statusClosed', 'Closed');
        case 'resolved':
            return t('tickets.statusResolved', 'Resolved');
        case 'pending':
            return t('tickets.statusPending', 'Pending');
        default:
            return raw;
    }
}

function Tickets({
    title,
    ticketBasePath = '/tickets',
    loadTickets: loadTicketsRequest = defaultLoadTickets,
    createTicket: createTicketRequest = defaultCreateTicket,
    loadSupportTickets: loadSupportTicketsRequest = defaultLoadSupportTickets,
}) {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const page = parseInt(searchParams.get('page') || '1', 10);
    const pageSize = parseInt(searchParams.get('pageSize') || '20', 10);
    const [tickets, setTickets] = useState([]);
    const [supportTickets, setSupportTickets] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [supportTotalCount, setSupportTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [loadingSupport, setLoadingSupport] = useState(false);
    const [creating, setCreating] = useState(false);
    const [activeTab, setActiveTab] = useState(() => (searchParams.get('tab') === 'support' ? 'support' : 'internal'));

    const [dialogOpen, setDialogOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const showSupportTabs = ticketBasePath === '/tickets';

    const loadTickets = useCallback(async () => {
        setLoading(true);
        try {
            const { items, total } = readPage(await loadTicketsRequest({ page, pageSize }));
            setTickets(items);
            setTotalCount(total);
        } catch (e) {
            console.error('Failed to load tickets', e);
            setSnackbar({ open: true, message: t('tickets.failedToLoadTickets', 'Failed to load tickets'), severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, [loadTicketsRequest, page, pageSize, t]);

    const loadSupportTickets = useCallback(async () => {
        setLoadingSupport(true);
        try {
            const { items, total } = readPage(await loadSupportTicketsRequest({ page, pageSize }));
            setSupportTickets(items);
            setSupportTotalCount(total);
        } catch (e) {
            console.error('Failed to load support tickets', e);
            setSnackbar({ open: true, message: t('tickets.failedToLoadSupportTickets', 'Failed to load support tickets'), severity: 'error' });
        } finally {
            setLoadingSupport(false);
        }
    }, [loadSupportTicketsRequest, page, pageSize, t]);

    useEffect(() => {
        loadTickets();
    }, [loadTickets]);

    useEffect(() => {
        if (showSupportTabs && activeTab === 'support') {
            loadSupportTickets();
        }
    }, [activeTab, loadSupportTickets, showSupportTabs]);

    const sortedTickets = useMemo(() => {
        const list = Array.isArray(tickets) ? [...tickets] : [];
        list.sort((a, b) => Number(b?.updatedAt || b?.createdAt || 0) - Number(a?.updatedAt || a?.createdAt || 0));
        return list;
    }, [tickets]);

    const sortedSupportTickets = useMemo(() => {
        const list = Array.isArray(supportTickets) ? [...supportTickets] : [];
        list.sort((a, b) => Number(b?.updatedAt || b?.createdAt || 0) - Number(a?.updatedAt || a?.createdAt || 0));
        return list;
    }, [supportTickets]);

    const handleCreate = async ({ title, type, description, estimatedHours }) => {
        setCreating(true);
        try {
            await createTicketRequest({ title, type: normalizeTicketType(type), description, estimatedHours });
            setDialogOpen(false);
            setSnackbar({ open: true, message: t('tickets.ticketCreated', 'Ticket created'), severity: 'success' });
            await loadTickets();
        } catch (e) {
            console.error('Failed to create ticket', e);
            setSnackbar({ open: true, message: t('tickets.failedToCreate', 'Failed to create ticket'), severity: 'error' });
            throw e;
        } finally {
            setCreating(false);
        }
    };

    const handleCloseSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));
    const isInternalTab = !showSupportTabs || activeTab === 'internal';
    const handleTabChange = (event, value) => {
        setActiveTab(value);
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (value === 'support') next.set('tab', 'support');
            else next.delete('tab');
            next.set('page', '1');
            return next;
        });
    };

    const handleChangePage = (nextPage) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('page', String(nextPage));
            return next;
        });
    };

    const handleChangePageSize = (nextSize) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('pageSize', String(nextSize));
            next.set('page', '1');
            return next;
        });
    };

    const ticketsLabel = (n) => (n === 1 ? t('tickets.ticketOne', 'ticket') : t('tickets.ticketOther', 'tickets'));
    const supportTicketsLabel = (n) => (n === 1 ? t('tickets.supportTicketOne', 'support ticket') : t('tickets.supportTicketOther', 'support tickets'));

    const renderPagination = (count, itemLabel) => (
        <Pagination
            count={count}
            page={page}
            pageSize={pageSize}
            onPageChange={handleChangePage}
            onPageSizeChange={handleChangePageSize}
            itemLabel={itemLabel}
        />
    );

    return (
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography variant="h5" fontWeight="bold">{title || t('sideBar.tickets', 'Tickets')}</Typography>
                    <HelpButton />
                </Box>
                {isInternalTab ? (
                    <Button variant="contained" onClick={() => setDialogOpen(true)} disabled={loading || creating}>
                        {t('tickets.newTicket', 'New Ticket')}
                    </Button>
                ) : null}
            </Box>

            {showSupportTabs ? (
                <Paper elevation={0} variant="outlined">
                    <Tabs value={activeTab} onChange={handleTabChange}>
                        <Tab value="internal" label={t('tickets.internalTab', 'Internal')} />
                        <Tab value="support" label={t('tickets.supportTab', 'Support')} />
                    </Tabs>
                </Paper>
            ) : null}

            {isInternalTab ? (
                <>
                    {loading ? <Alert severity="info">{t('tickets.loadingTickets', 'Loading tickets…')}</Alert> : null}

                    {!loading && totalCount === 0 ? (
                        <Paper sx={{ p: 2 }} elevation={1}>
                            <Typography variant="body1">{t('tickets.noTicketsFound', 'No tickets found.')}</Typography>
                        </Paper>
                    ) : (
                        <TableContainer component={Paper} elevation={1}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t('tickets.ticketNumber', 'Ticket #')}</TableCell>
                                        <TableCell>{t('tickets.title', 'Title')}</TableCell>
                                        <TableCell>{t('tickets.type', 'Type')}</TableCell>
                                        <TableCell>{t('tickets.createdBy', 'Created By')}</TableCell>
                                        <TableCell>{t('tickets.assignedTo', 'Assigned To')}</TableCell>
                                        <TableCell>{t('tickets.status', 'Status')}</TableCell>
                                        <TableCell>{t('tickets.priority', 'Priority')}</TableCell>
                                        <TableCell>{t('tickets.estimatedHours', 'Estimated Hours')}</TableCell>
                                        <TableCell>{t('tickets.createdAt', 'Created At')}</TableCell>
                                        <TableCell>{t('tickets.updatedAt', 'Updated At')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sortedTickets.map((row) => (
                                        <TableRow key={row.ticketId || `${row.title}-${row.createdAt}`}>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                {row.ticketId ? (
                                                    <Typography
                                                        component={Link}
                                                        to={`${ticketBasePath}/${encodeURIComponent(row.ticketId)}`}
                                                        sx={{ color: 'primary.main', textDecoration: 'none' }}
                                                    >
                                                        {row.ticketId}
                                                    </Typography>
                                                ) : (
                                                    '-'
                                                )}
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    whiteSpace: 'nowrap',
                                                    textDecoration: isClosedStatus(row.status) ? 'line-through' : 'none',
                                                    color: isClosedStatus(row.status) ? 'text.secondary' : 'text.primary',
                                                }}
                                            >
                                                {row.title || t('tickets.untitled', '(untitled)')}
                                            </TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{typeLabel(t, row.type)}</TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.createdBy || '-'}</TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.assignedTo || '-'}</TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{statusLabel(t, row.status)}</TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                <Chip
                                                    label={priorityLabel(t, row.priority)}
                                                    size="small"
                                                    sx={{
                                                        minWidth: 76,
                                                        height: 24,
                                                        fontWeight: 700,
                                                        ...getPrioritySx(row.priority),
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatEstimatedHours(row.estimatedHours)}</TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatEpoch(row.createdAt)}</TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatEpoch(row.updatedAt)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            {renderPagination(totalCount, ticketsLabel(totalCount))}
                        </TableContainer>
                    )}
                </>
            ) : (
                <>
                    {loadingSupport ? <Alert severity="info">{t('tickets.loadingSupportTickets', 'Loading support tickets…')}</Alert> : null}

                    {!loadingSupport && supportTotalCount === 0 ? (
                        <Paper sx={{ p: 2 }} elevation={1}>
                            <Typography variant="body1">{t('tickets.noSupportTicketsFound', 'No support tickets found.')}</Typography>
                        </Paper>
                    ) : (
                        <TableContainer component={Paper} elevation={1}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t('tickets.supportTicketNumber', 'Support #')}</TableCell>
                                        <TableCell>{t('tickets.title', 'Title')}</TableCell>
                                        <TableCell>{t('tickets.requesterEmail', 'Requester Email')}</TableCell>
                                        <TableCell>{t('tickets.status', 'Status')}</TableCell>
                                        <TableCell>{t('tickets.source', 'Source')}</TableCell>
                                        <TableCell>{t('tickets.conversationId', 'Conversation ID')}</TableCell>
                                        <TableCell>{t('tickets.createdAt', 'Created At')}</TableCell>
                                        <TableCell>{t('tickets.updatedAt', 'Updated At')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sortedSupportTickets.map((row) => (
                                        <TableRow key={row.supportTicketId || `${row.title}-${row.createdAt}`}>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                {row.supportTicketId ? (
                                                    <Typography
                                                        component={Link}
                                                        to={`/support-tickets/${encodeURIComponent(row.supportTicketId)}`}
                                                        sx={{ color: 'primary.main', textDecoration: 'none' }}
                                                    >
                                                        {row.supportTicketId}
                                                    </Typography>
                                                ) : (
                                                    '-'
                                                )}
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    minWidth: 220,
                                                    maxWidth: 360,
                                                    textDecoration: isClosedStatus(row.status) ? 'line-through' : 'none',
                                                    color: isClosedStatus(row.status) ? 'text.secondary' : 'text.primary',
                                                }}
                                            >
                                                {row.title || t('tickets.untitled', '(untitled)')}
                                            </TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.requesterEmail || '-'}</TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{statusLabel(t, row.status)}</TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.source || '-'}</TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.conversationId || '-'}</TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatEpoch(row.createdAt)}</TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatEpoch(row.updatedAt)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            {renderPagination(supportTotalCount, supportTicketsLabel(supportTotalCount))}
                        </TableContainer>
                    )}
                </>
            )}

            <NewTicketDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onCreated={handleCreate}
                creating={creating}
            />

            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={handleCloseSnackbar}>
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default Tickets;
